import { Product } from "../../common/models/product";
import { DisplayProduct } from "./dedupe-helpers";
import {
  extractMaterialString,
  formatDimensions,
  getMatchAssessment,
} from "./helpers";
import { MatchQualityBadge } from "./MatchQualityBadge";
import { useLocation, useNavigate } from "react-router-dom";
import { Price } from "./InventoryDetailsPrice";
import { BookmarkIcon } from "@heroicons/react/20/solid";
import { useDimensionsUnit } from "../../buyerStateDefs";
import { NewProductsNotificationForm } from "./NewProductsNotificationForm";
import useFirebaseAuthentication from "../../common/hooks/useFirebaseAuthentication";

const MID_RESULT_FORM_INDEX = 3;

const SearchResultsList = ({
  products,
  structuredQuery,
  trackSearchResultClick,
}: {
  products: Product[];
  structuredQuery: any;
  trackSearchResultClick: (
    searchResultRank: number,
    product: Product,
    query: string,
  ) => void;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dimensionUnit = useDimensionsUnit((state) => state.dimensionsUnit);
  const { authUser } = useFirebaseAuthentication();

  const savedProducts = JSON.parse(
    localStorage.getItem("savedProducts") || "[]",
  ) as Product[];

  const getMatchQualityElement = (p: Product) => (
    <MatchQualityBadge product={p} structuredQuery={structuredQuery} />
  );

  const targetPriceUnit = ["ft", "in"].includes(dimensionUnit) ? "lb" : "kg";
  const notificationFormIndex = (() => {
    const firstNonGreenMatchIndex = products.findIndex(
      (product) => getMatchAssessment(product, structuredQuery).totalLevels > 0,
    );

    if (firstNonGreenMatchIndex === -1) return products.length;
    if (firstNonGreenMatchIndex === 0)
      return Math.min(MID_RESULT_FORM_INDEX, products.length);

    return firstNonGreenMatchIndex;
  })();

  // Why several similar rows exist (differing standard/condition/origin), and
  // how many identical cheaper-or-equal offers a row replaced. Both come from
  // the display-level dedupe in dedupe-helpers.ts.
  const offerAnnotations = (p: DisplayProduct) =>
    (p.distinguishingSpecs?.length || p.collapsedIdenticalOffers) && (
      <div className="mt-1 flex flex-wrap items-center gap-1 font-normal">
        {p.distinguishingSpecs?.map((spec) => (
          <span
            key={spec.label}
            className="inline-block rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800"
          >
            {spec.label}: {spec.value}
          </span>
        ))}
        {!!p.collapsedIdenticalOffers && (
          <span className="text-xs text-gray-400">
            Best price of {p.collapsedIdenticalOffers + 1} identical offers
          </span>
        )}
      </div>
    );

  const displayRow = (p: DisplayProduct, index: number) => (
    <tr
      key={p.id}
      data-search-result=""
      className="hover:bg-slate-100 active:bg-slate-200 cursor-pointer"
      onClick={() => {
        trackSearchResultClick(index + 1, p, structuredQuery.rawQuery);
        navigate(
          `/product?id=${p.id}&q=${new URLSearchParams(location.search).get("q") || ""}`,
          { state: { previousUrl: window.location.href } },
        );
      }}
    >
      <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:w-auto sm:max-w-none sm:pr-0">
        <div className="max-w-64">
          {extractMaterialString(p)}{" "}
          {savedProducts.some((sp) => sp.id === p.id) && (
            <BookmarkIcon className="w-3 h-3 inline ml-1" />
          )}
        </div>
        {offerAnnotations(p)}
        <dl className="font-normal lg:hidden">
          <dt className="sr-only sm:hidden">Match Quality</dt>
          <dd className="mt-1 truncate text-gray-500 sm:hidden">
            {getMatchQualityElement(p)}
          </dd>
          <dt className="sr-only lg:hidden">Shape</dt>
          <dd className="mt-1 truncate text-gray-500 lg:hidden">{p.shape}</dd>
          <dt className="sr-only lg:hidden">Dimensions</dt>
          <dd className="mt-1 truncate text-gray-500 lg:hidden">
            {formatDimensions(p, dimensionUnit)}
          </dd>
          {!!p.price && (
            <>
              <dt className="sr-only md:hidden">Price</dt>
              <dd className="mt-1 text-gray-500 md:hidden">
                <Price product={p} targetPriceUnit={targetPriceUnit} />
              </dd>
            </>
          )}
          {/* TEMPORARILY DISABLED */}
          {/* <dt className="sr-only sm:hidden">Location</dt>
          <dd className="mt-1 truncate text-gray-500 sm:hidden">
            {p.location}
          </dd> */}
        </dl>
      </td>
      <td className="hidden px-3 py-4 text-sm text-gray-500 lg:table-cell">
        <dt className="sr-only hidden lg:block">Shape</dt>
        <dd className="hidden mt-1 truncate text-gray-500 lg:block">
          {p.shape}
        </dd>
        <dt className="sr-only hidden lg:block">Dimensions</dt>
        <dd className="mt-1 truncate text-gray-500 hidden lg:block">
          {formatDimensions(p, dimensionUnit)}
        </dd>
      </td>
      <td className="hidden px-3 py-4 text-sm text-gray-500 md:table-cell">
        {!!p.price && (
          <>
            <dt className="sr-only hidden md:block">Price</dt>
            <dd className="mt-1 text-gray-500 hidden md:block">
              <Price product={p} targetPriceUnit={targetPriceUnit} />
            </dd>
          </>
        )}
      </td>
      {/* TEMPORARILY DISABLED */}
      {/* <td className="px-3 py-4 text-sm text-gray-500 hidden sm:table-cell">
        <dt className="sr-only hidden sm:block">Location</dt>
        <dd className="mt-1 truncate text-gray-500 hidden sm:block">
          {p.location}
        </dd>
        {p.supplierName && (
          <>
            <dt className="sr-only hidden sm:block">Supplier</dt>
            <dd className="mt-1 truncate text-gray-500 hidden sm:block">
              {p.supplierName}
            </dd>
          </>
        )}
      </td> */}
      <td className="py-4 pl-3 pr-4 text-right text-sm font-medium hidden sm:block">
        {getMatchQualityElement(p)}
        <div
          id={`search-result-annotations-${p.id}`}
          className="inline-block pl-3"
        ></div>
      </td>
    </tr>
  );

  return (
    <>
      <div>
        <div className="-mx-4 sm:-mx-0">
          <table className="min-w-full divide-y divide-gray-100">
            <thead>
              <tr className="lg:sticky lg:top-[10em] z-10 bg-white">
                <th
                  scope="col"
                  className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900"
                >
                  Material
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 lg:table-cell"
                >
                  Shape & Dimensions
                </th>
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 md:table-cell"
                >
                  Price
                </th>
                {/* TEMPORARILY DISABLED */}
                {/* <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-left text-sm font-semibold text-gray-900 sm:table-cell"
                >
                  Location
                </th> */}
                <th
                  scope="col"
                  className="hidden px-3 py-3.5 text-right text-sm font-semibold text-gray-900 sm:table-cell"
                >
                  Match Quality
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {products
                .slice(0, notificationFormIndex)
                .map((p, i) => displayRow(p, i))}
              {!authUser && (
                <tr>
                  <td colSpan={5}>
                    <NewProductsNotificationForm
                      searchResults={products}
                      query={structuredQuery.rawQuery}
                      compact={true}
                    />
                  </td>
                </tr>
              )}
              {products
                .slice(notificationFormIndex)
                .map((p, i) => displayRow(p, i + notificationFormIndex))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default SearchResultsList;
